import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  BorrowerCaseListResponse,
  InvitationExchangeResponse,
  InvitationIssuedResponse,
  OutboxEmailsResponse,
} from '@tex/finance-shared'
import { apiGet, apiSend } from './api'
import { caseKeys } from './cases'
import { ME_KEY } from './session'

const IDEMPOTENCY_HEADER = 'Idempotency-Key'

export const borrowerKeys = {
  cases: ['me', 'cases'] as const,
}

/** Cases the signed-in user reaches as a verified borrower participant. */
export function useMyCases(enabled = true) {
  return useQuery({
    queryKey: borrowerKeys.cases,
    enabled,
    queryFn: () => apiGet<BorrowerCaseListResponse>('/api/v1/me/cases'),
  })
}

function useParticipantMutation<TVars, TResult>(caseId: string, fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
  })
}

/** `idempotencyKey` is minted per attempt-series so a retry replays the same link instead of minting another. */
export function useInviteParticipant(caseId: string, idempotencyKey: string) {
  return useParticipantMutation(caseId, (input: { email?: string; display_name?: string | null }) =>
    apiSend<InvitationIssuedResponse>('POST', `/api/v1/cases/${caseId}/invitations`, input, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
  )
}

export function useResendInvitation(caseId: string, idempotencyKey: string) {
  return useParticipantMutation(caseId, (participantId: string) =>
    apiSend<InvitationIssuedResponse>('POST', `/api/v1/cases/${caseId}/invitations/${participantId}/resend`, undefined, {
      [IDEMPOTENCY_HEADER]: idempotencyKey,
    }),
  )
}

export function useRevokeParticipant(caseId: string) {
  return useParticipantMutation(caseId, (vars: { participantId: string; reason: string }) =>
    apiSend<{ participant: unknown }>('POST', `/api/v1/cases/${caseId}/invitations/${vars.participantId}/revoke`, { reason: vars.reason }),
  )
}

/** Turns a share-link token into a verified participation for the signed-in user. */
export function useExchangeInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => apiSend<InvitationExchangeResponse>('POST', '/api/v1/invitations/exchange', { token }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ME_KEY }), qc.invalidateQueries({ queryKey: borrowerKeys.cases })])
    },
  })
}

/** Demo-mode only; 404s in production and 403s for anyone but Tex ops_admin. */
export function useOutboxEmails(to: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['outbox', 'emails', to ?? ''],
    enabled,
    retry: false,
    queryFn: () => apiGet<OutboxEmailsResponse>(`/api/v1/outbox/emails${to ? `?to=${encodeURIComponent(to)}` : ''}`),
  })
}
